import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUninterestedTutorialIds,
  markTutorialAsUninterested,
  unmarkTutorialAsUninterested,
} from "@/lib/supabase";

export function useUninterestedTutorials(userId?: string) {
  const queryClient = useQueryClient();
  const queryKey = ["uninterested", userId];

  // Query for uninterested tutorials
  const {
    data: uninterestedIds = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => getUninterestedTutorialIds(userId!),
    enabled: !!userId,
  });
  
  // Mutation for marking as uninterested
  const { mutate: markUninterested } = useMutation({
    mutationFn: async (documentId: string) => {
      await markTutorialAsUninterested(userId!, documentId);
      return documentId;
    },
    onMutate: async (documentId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousIds = queryClient.getQueryData<string[]>(queryKey) ?? [];

      // Optimistically update to the new value
      queryClient.setQueryData<string[]>(queryKey, (old) => [
        ...(old ?? []),
        documentId,
      ]);

      return { previousIds };
    },
    onError: (err, documentId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(queryKey, context?.previousIds);
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation for unmarking as uninterested
  const { mutate: unmarkUninterested } = useMutation({
    mutationFn: async (documentId: string) => {
      await unmarkTutorialAsUninterested(userId!, documentId);
      return documentId;
    },
    onMutate: async (documentId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousIds = queryClient.getQueryData<string[]>(queryKey) ?? [];
      queryClient.setQueryData<string[]>(
        queryKey,
        (old) => old?.filter((id) => id !== documentId) ?? []
      );
      return { previousIds };
    },
    onError: (err, documentId, context) => {
      queryClient.setQueryData(queryKey, context?.previousIds);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const isUninterested = (documentId: string) =>
    uninterestedIds.includes(documentId);

  const toggleUninterested = (documentId: string) => {
    if (isUninterested(documentId)) {
      unmarkUninterested(documentId);
    } else {
      markUninterested(documentId);
    }
  };

  return {
    uninterestedIds,
    isUninterested,
    toggleUninterested,
    // Expose query status fields for consumers
    isLoading,
    isError,
    error,
  };
}
